import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import HeaderBread from "../../../components/HeaderBread";
import { useDataLayerValue } from "../../../DataLayer/DataLayer";
import Layout from "../../../Layout";

import { message } from "antd";
import Swal from "sweetalert2";
import CustomHeaderTitle from "../../../components/constants/CustomHeaderTitle";
import DeletePop from "../../../constants/ErrorPermissionModal";

import YouTubeEmbed from "../../../components/Banner/YouTubeEmbed";
import Logs from "../../../components/Logs";
import SwitchBar from "../../../components/Pages/SwitchBar";
import SearchField from "../../../components/SearchField";
import ActiveSwitchFile from "../../../components/SwitchFile";
import CustomizedTable from "../../../components/UI/CustomizedTable";
import { constants } from "../../../constants/constants";
import { addlog } from "../../../utils/ApiCalls/Logs";
import {
  deleteActivityCategory,
  getActivityCategory,
  updateActivitySingleCategory,
  updatePosition,
} from "../../../utils/ApiCalls/Mobile/ActivityMasters";
import { getVessels } from "../../../utils/ApiCalls/SpecialPage";
import { checkPermission } from "../../../utils/Validation/checkPermission";
import SubActivityCategory from "./SubActivityCategory";
import { LOG_ACTIONS, LOG_TYPES } from "../../../utils/Constant/LogConstant";

const ActivityCategoryMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [{ userDetails, permission }, dispatch] = useDataLayerValue();

  const firstLoad = useRef(true);
  const prevVesselRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [active, setActive] = useState("listing");
  const [vesselOptions, setVesselOptions] = useState([]);
  const [vesselId, setVesselId] = useState(null);
  const [columns, setColumns] = useState([]);
  const [sort, setSort] = useState("id");
  const [order, setOrder] = useState("desc");
  const [activeCat, setActiveCat] = useState(null);


  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(location.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(location.search).get("pageSize")) || 10
  );
  const [search, setSearch] = useState(
    () => new URLSearchParams(location.search).get("search") || ""
  );

const refreshParentCategories = async () => {
  await getData({ page, pageSize, vesselId, search, sort, order });
};


  // STEP 1 — Load params from URL on first render
  useEffect(() => {
    if (firstLoad.current) {
      const q = new URLSearchParams(location.search);
      const pageParam = parseInt(q.get("page")) || 1;
      const pageSizeParam = parseInt(q.get("pageSize")) || 10;
      const searchParam = q.get("search") || "";
      const vesselParam = q.get("vesselId");

      setPage(pageParam);
      setPageSize(pageSizeParam);
      setSearch(searchParam);
      if (vesselParam) setVesselId(Number(vesselParam));

      firstLoad.current = false;
    }
  }, [location.search]);

  // STEP 2 — Keep URL synced with internal state
  useEffect(() => {
    if (firstLoad.current) return;
    const q = new URLSearchParams(location.search);

    const currentParams = {
      page: parseInt(q.get("page")) || 1,
      pageSize: parseInt(q.get("pageSize")) || 10,
      search: q.get("search") || "",
      vesselId: q.get("vesselId") ? Number(q.get("vesselId")) : null,
    };

    if (
      currentParams.page !== page ||
      currentParams.pageSize !== pageSize ||
      currentParams.search !== search ||
      currentParams.vesselId !== vesselId
    ) {
      q.set("page", page);
      q.set("pageSize", pageSize);
      if (search) q.set("search", search);
      else q.delete("search");
      if (vesselId) q.set("vesselId", vesselId);
      else q.delete("vesselId");
      navigate({ search: q.toString() }, { replace: true });
    }
  }, [page, pageSize, search, vesselId]);

  // STEP 3 — Reset to page 1 when vessel changes
  useEffect(() => {
    if (prevVesselRef.current && prevVesselRef.current !== vesselId) {
      setPage(1);
      const q = new URLSearchParams(location.search);
      q.set("page", 1);
      q.set("vesselId", vesselId);
      navigate({ search: q.toString() }, { replace: true });
    }
    prevVesselRef.current = vesselId;
  }, [vesselId]);

  // STEP 4 — Fetch vessels and set default vessel if not present in URL
  const getOptionLocation = async () => {
    if (!permission?.permission_json) return;

    dispatch({ type: "SET_LOADING", loading: true });
    let vessels = await getVessels();

    if (vessels.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: vessels.error });
    } else {
      let vesselData = vessels.filter((item) =>
        checkPermission(
          permission,
          "read_per",
          "Categories",
          window?.location?.pathname + `/${item.name.toLowerCase().replaceAll(" ", "-")}`
        )
      );

      setVesselOptions(vesselData);

      const q = new URLSearchParams(location.search);
      const urlVesselId = q.get("vesselId");
      const parsedUrlVesselId = urlVesselId ? Number(urlVesselId) : null;
      const hasAllowedUrlVessel =
        parsedUrlVesselId &&
        vesselData.some((item) => item.id === parsedUrlVesselId);
      const resolvedVesselId = hasAllowedUrlVessel
        ? parsedUrlVesselId
        : vesselData?.[0]?.id ?? null;

      if (resolvedVesselId) {
        setVesselId(resolvedVesselId);
      } else {
        setVesselId(null);
      }

      if (!hasAllowedUrlVessel && resolvedVesselId) {
        q.set("vesselId", resolvedVesselId);
        q.set("page", 1);
        q.set("pageSize", pageSize);
        if (search) q.set("search", search);
        else q.delete("search");

        navigate({ search: q.toString() }, { replace: true });
      }
    }

    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    getOptionLocation();
  }, [permission]);

  // STEP 5 — Fetch category data when vessel/page/search/sort/order changes
  useEffect(() => {
    if (vesselId)
      getData({ page, pageSize, vesselId, search, sort, order });
  }, [vesselId, page, pageSize, search, sort, order]);

  // ========================== API HANDLERS ==========================
  const getData = async ({ page, pageSize, vesselId, search, sort, order }) => {
    dispatch({ type: "SET_LOADING", loading: true });

    let data = await getActivityCategory({
      page,
      limit: pageSize,
      vessel_id: vesselId,
      search,
      sort,
      order,
      parent_check: "true",
    });

    if (data.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
    } else if (data?.data) {
      setRows(data.data);
      setCount(data.count);
    }

    dispatch({ type: "SET_LOADING", loading: false });
  };

  const sortHandler = async (options) => {
    if (Object.keys(options).length > 0) {
      const sort = options.columnKey;
      const order =
        options.order === "ascend"
          ? "asc"
          : options.order === "descend"
            ? "desc"
            : undefined;

      setSort(sort);
      setOrder(order);
    }
  };

  const handleEdit = (id) => {
    if (checkPermission(permission, "update_per", "Categories", window?.location?.pathname)) {
      const q = new URLSearchParams(location.search);
      q.set("page", page);
      q.set("pageSize", pageSize);
      if (vesselId) q.set("vesselId", vesselId);
      if (search) q.set("search", search);

      navigate(`/shore/mobile-masters/activity-categories/edit/${id}?${q.toString()}`);
    } else {
      message.error("Update permission missing");
    }
  };

  const handleDelete = async (id, name, vesselId) => {
    if (checkPermission(permission, "delete_per", "Categories", window?.location?.pathname)) {
      let val = await deleteActivityCategory(id);
      if (val.error) {
        Swal.fire({ icon: "error", title: "Oops...", text: val.error });
      } else {
        addlog(
          LOG_ACTIONS.REMOVED,
          LOG_TYPES.CATEGORIES,
          {
            id,
            title: `Category ${name} from vessel ${vesselOptions.find(v => v.id === vesselId)?.name}` 
          }, userDetails.id);
        await getData({ page, pageSize, vesselId, search, sort, order });
      }
    } else {
      message.error("Missing Permission for delete");
    }
  };

  const updateBannerPosition = async (banners) => {
    if (checkPermission(permission, "update_per", "Categories", window?.location?.pathname)) {
      try {
        const args = { search, page, limit: pageSize, vesselId };
        const data = await updatePosition(banners, args);
        addlog("Categories Position Updated", LOG_TYPES.CATEGORIES, {}, userDetails.id);

        if (data.error) {
          Swal.fire({ icon: "error", title: "Oops...", text: data.error });
        } else if (data.data) {
          setRows(data.data);
          setCount(data.count);
        }
      } catch (error) {
        Swal.fire({ icon: "error", title: "Oops...", text: error.message || "Unknown error" });
      }
    } else {
      message.error("Update permission missing");
    }
  };

  const handleUpdate = async (id, values, title) => {
    if (checkPermission(permission, "update_per", "Categories", window?.location?.pathname)) {
      let data = await updateActivitySingleCategory(id, values);
      if (data.error) {
        Swal.fire({ icon: "error", title: "Oops...", text: data.error });
      } else {
         addlog(
           LOG_TYPES.CATEGORIES+" "+
          (!values.valid_from
            ? values.is_active === 1
              ? LOG_ACTIONS.ACTIVATED
              : LOG_ACTIONS.DEACTIVATED
            : LOG_ACTIONS.UPDATED),
          LOG_TYPES.CATEGORIES,
          { ...values, id, title: title || values.name },
          userDetails.id
        );
      }
      await getData({ page, pageSize, vesselId, search, sort, order });
    } else {
      message.error("Update permission missing");
    }
  };

  // ========================== TABLE COLUMNS ==========================
  useEffect(() => {
    setColumns([
      {
        title: "Sl",
        width: 10,
        key: "id",
        fixed: "left",
        render: (_, __, index) => (page - 1) * pageSize + index + 1,
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        sorter: true,
        width: 100,
        render: (item) => <div dangerouslySetInnerHTML={{ __html: item }} />,
      },
      {
        title: "Image",
        width: 30,
        dataIndex: "image",
        key: "image",
        render: (image) => (
          <img
            alt="banner"
            src={
              image
                ? `${constants.MEDIAURL}/${image}`
                : "https://ralfvanveen.com/wp-content/uploads/2021/06/Placeholder-_-Glossary.svg"
            }
            style={{ width: "100px", height: "80px" }}
          />
        ),
      },
      {
        title: "Sub Category",
        dataIndex: "pageCount",
        key: "pageCount",
        render: (item, record) =>
          record?.pageCount > 0 && (
            <SubActivityCategory
              vesselId={vesselId}
              activeCat={activeCat}
              setActiveCat={setActiveCat}
              record={record}
              refreshParentCategories={refreshParentCategories}
            />
          ),
      },
      {
        title: "Status",
        dataIndex: "is_active",
        key: "is_active",
        render: (item, record, index) => (
          <ActiveSwitchFile
            item={item}
            record={record}
            index={index}
            handleUpdate={handleUpdate}
          />
        ),
      },
      {
        title: "Action",
        key: "operation",
        width: 100,
        render: (_, record) => (
          <div className="table_action_btn">
            <button className="btn" onClick={() => handleEdit(record.id)}>
              <i className="ri-edit-2-line" title="Edit"></i>
            </button>
            <DeletePop onConfirm={() => handleDelete(record.id,record.name,vesselId)} title={`Delete ${record.name ?? "this"} Category`}>
              <button className="btn delete_btn">
                <i className="ri-delete-bin-line" title="Delete"></i>
              </button>
            </DeletePop>
          </div>
        ),
      },
    ]);
  }, [page, pageSize, vesselId, activeCat,vesselOptions]);

  const navigation = [
    {
      title: "Home",
      route: "/",
    },
    {
      title: "Categories",
      route: `/shore/mobile-masters/activity-categories${location.search}`,
    },
  ];

  return (
    <Layout type={"read_per"} module_data={"Categories"}>
      <CustomHeaderTitle title={"Categories"} />
      <div>
        <div>
          <HeaderBread
            navigation={navigation}
            title={"Categories"}
          ></HeaderBread>
        </div>
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">
            <SwitchBar
              tabs={[
                { title: "Categories List", value: "listing" },
                { value: "logs", title: " History Logs" },
              ]}
              activeTab={active}
              setActiveTab={setActive}
            />
            {active == "listing" && (
              <div className="card">
                <div className="card-header two_item">
                  <h5 className="card-title">Categories List</h5>
                  <div className="two_item">
                    <Link
                      to={`/shore/mobile-masters/activity-categories/add${location.search}`}
                      className="btn add_v_btn mx-2"
                    >
                      Add Categories
                    </Link>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table_holder">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                      }}
                    >
                      {/* <SwitchBar
                        tabs={vesselOptions?.map((item) => ({
                          title: item.name,
                          value: item.id,
                        }))}
                        activeTab={vesselId}
                        setActiveTab={(val) => {
                          setVesselId(val);

                          setPage(1); //THIS IS NOT STATIC VALUE, IT RESETS THE VALUE OF PAGE TO 1 IF VESSEL CHANGES ++++++++++++

                          const q = new URLSearchParams(location.search);
                          q.set("vesselId", val);
                          q.set("page", 1); // ENSURE URL MATCH ++++++++++++++++++++++++++++++
                          navigate({ search: q.toString() }, { replace: true });
                        }}
                      /> */}
                      <SwitchBar
                        tabs={vesselOptions?.map((item) => ({
                          title: item.name,
                          value: item.id,
                        }))}
                        activeTab={vesselId}
                        setActiveTab={(val) => {
                          setVesselId(val);
                          const q = new URLSearchParams(location.search);
                          q.set("vesselId", val);
                          navigate({ search: q.toString() }, { replace: true });
                        }}
                      />

                      <div style={{ maxWidth: 200 }}>
                        <SearchField
                          value={search}
                          SearchValue={() => {
                            setPage(page);
                          }}
                          setValue={(e) => {
                            setSearch(e.target.value);
                          }}
                        />
                      </div>
                    </div>
                    {rows && columns && (
                      <CustomizedTable
                        setRows={setRows}
                        setPageSize={setPageSize}
                        setPage={setPage}
                        pageSize={pageSize}
                        page={page}
                        count={count}
                        columns={columns}
                        rows={rows}
                        title={"Crisis Page"}
                        sortHandler={sortHandler}
                        updatePosition={updateBannerPosition}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
        {active === "logs" && (
          <Logs
            type={"Categories"}
            slug={"/shore/mobile-masters/activity-categories/edit/"}
          />
        )}
      </div>
    </Layout>
  );
};

export default ActivityCategoryMaster;
